import { motion, AnimatePresence, Variants } from "framer-motion";
import { isEmpty } from "lodash";
import React, {
  useState,
  useRef,
  useEffect,
  forwardRef,
  ReactNode,
  ElementType,
} from "react";
import { useTranslation } from "react-i18next";
import SVG from "react-inlinesvg";
import { useLocation, useNavigate } from "react-router-dom";

import { useMobile } from "@krea/customer-features/hooks";
import CloseSVG from "@krea/customer-features/images/svg/close.svg";
import UserSVG from "@krea/customer-features/images/svg/user.svg";
import { settings } from "@krea/customer-features/settings";
import Language from "@krea/customer-features/shared-components/app/language";
import Logo from "@krea/customer-features/shared-components/app/logo";
import Button from "@krea/customer-features/shared-components/button";
import Container from "@krea/customer-features/shared-components/container";
import CustomerServiceContactResponsiveDialog from "@krea/customer-features/shared-components/modals/CustomerServiceContact";
import { useLogout } from "@krea/customer-features/store/user/hooks/mutations/useLogout";
import { useGetUserData } from "@krea/customer-features/store/user/hooks/queries/useGetUserData";
import { twMerge } from "@krea/shared/design-system/tailwind-utils";
import { CountryCode } from "@krea/shared/types/common";

import { ChangeApplicationModal } from "../application-selector/ChangeApplicationModal";

import { MenuContext } from "./header-context";

const motionBackdrop: Variants = {
  hidden: {
    opacity: 0,
    zIndex: 0,
  },
  visible: {
    opacity: 1,
    zIndex: 200000,
  },
};

const motionMenu: Variants = {
  hidden: {
    opacity: 0,
    transitionEnd: {
      visibility: "hidden",
      zIndex: 0,
    },
    transition: {
      duration: 0.4,
      ease: "easeOut",
    },
  },
  visible: {
    opacity: 1,
    visibility: "visible",
    zIndex: 200001,
    transition: {
      duration: 0.4,
      ease: "easeOut",
    },
  },
};

const logoStyling = (countryCode: CountryCode) => {
  switch (countryCode) {
    case CountryCode.SE:
      return "lg:tw-w-[82px] tw-w-[74px]";
    case CountryCode.FI:
      return "lg:tw-w-[112px] tw-w-[104px]";
  }
};

interface IMenuButtonProps {
  className?: string;
  onClick?: () => void;
  icon: string;
}

const MenuButton = forwardRef<HTMLButtonElement, IMenuButtonProps>(
  ({ className, onClick, icon }, ref) => {
    return (
      <button
        ref={ref}
        className={twMerge(
          "tw-bg-transparent xl:tw-invisible xl:tw-m-0 xl:tw-ml-[132px]",
          "tw-flex tw-justify-center tw-items-center tw-p-2",
          className,
        )}
        onClick={onClick}
        aria-label="Menu"
      >
        <div className="tw-relative tw-h-[22px] tw-w-[22px] [&_svg]:tw-left-0 [&_svg]:tw-top-0">
          <SVG className="tw-absolute tw-h-auto tw-w-full" src={icon} />
        </div>
      </button>
    );
  },
);

interface IMobileMenuDrawerProps {
  onClose: () => void;
  children: React.ReactNode;
  fullScreen?: boolean;
}

const MobileMenuDrawer = forwardRef<HTMLDivElement, IMobileMenuDrawerProps>(
  ({ onClose, children, fullScreen }, ref) => {
    return (
      <div
        ref={ref}
        className={twMerge(
          "tw-top-0 tw-left-0 tw-shadow-md tw-z-[10001]",
          "tw-flex-col tw-fixed tw-max-h-full tw-bg-white tw-flex tw-w-full",
          {
            "tw-h-full": fullScreen,
          },
        )}
      >
        <div
          className={twMerge(
            "lg:tw-h-16 tw-h-14",
            "tw-grow-0 tw-shrink-0 tw-border-b tw-w-full",
          )}
        >
          <Container fluid className="tw-h-full">
            <div className="tw-flex tw-h-full tw-items-center tw-justify-between tw-text-primary">
              <Logo className={logoStyling(settings.countryCode)} />

              <MenuButton
                icon={CloseSVG}
                className="-tw-mr-2"
                onClick={onClose}
              />
            </div>
          </Container>
        </div>

        <div className="tw-w-full tw-shrink tw-grow tw-overflow-auto">
          <Container>
            <div className="tw-w-full tw-py-6">{children}</div>
          </Container>
        </div>
      </div>
    );
  },
);

// TODO: Simplify this component
interface IHeaderProps {
  MiddleNavbarComponent?: ElementType;
  applicationSelector: ReactNode;
}

export const Header = ({
  MiddleNavbarComponent,
  applicationSelector,
}: IHeaderProps) => {
  const { t } = useTranslation();
  const { pathname } = useLocation();

  const isMobile = useMobile();
  const navigate = useNavigate();

  const refHeader = useRef<HTMLHeadElement>(null);
  const navRef = useRef<HTMLDivElement>(null);
  const refMenu = useRef<HTMLDivElement>(null);

  const [sticky, setSticky] = useState(false);
  const [mobileMenu, setMobileMenu] = useState(false);
  const [stepsMenu, setStepsMenu] = useState(false);
  const [scrollDown, setScrollDown] = useState(false);

  const { data: user } = useGetUserData();
  const { mutate: logout } = useLogout();

  const isDelegate = pathname === "/delegated-information";
  const isIdentifyCustomer = pathname.includes("/identify");

  const isNotLoginPage = pathname !== "/login";
  const isNotPreliminaryBidForm = !pathname.includes("/amount-estimator");
  const isPublicRoute = settings.unauthorizedPaths.includes(pathname);

  const mainLogoHref = isPublicRoute ? settings.countryWebsiteUrl : "/";

  const toggleMenu = () => {
    setMobileMenu(!mobileMenu);
  };

  const toggleStepsMenu = () => {
    setStepsMenu(!stepsMenu);
  };

  const onLogout = () => {
    logout("/login");
  };

  const [openContactDrawer, setOpenContactDrawer] = useState(false);
  const [isApplicationSelectorOpen, setIsApplicationSelectorOpen] =
    useState(false);

  const onContactCustomerService = () => {
    if (isMobile) {
      toggleMenu();
    }
    setOpenContactDrawer(!openContactDrawer);
  };

  useEffect(() => {
    function scrollCalc() {
      if (refHeader.current?.offsetHeight) {
        setSticky(window.scrollY > refHeader.current.offsetHeight);
      }
    }

    scrollCalc();

    window.addEventListener("scroll", scrollCalc);

    return () => {
      window.removeEventListener("scroll", scrollCalc);
    };
  }, []);

  useEffect(() => {
    let lastScrollTop = 0;

    function UpDownScroll() {
      const st = window.scrollY || document.documentElement.scrollTop;

      if (st > lastScrollTop) {
        // downscroll code
        setScrollDown(true);
      } else {
        // upscroll code
        setScrollDown(false);
      }
      lastScrollTop = st <= 0 ? 0 : st; // For Mobile or negative scrolling
    }

    if (sticky) {
      UpDownScroll();
      window.addEventListener("scroll", UpDownScroll);
    }

    return () => {
      window.removeEventListener("scroll", UpDownScroll);
    };
  }, [sticky]);

  useEffect(() => {
    function handleClickOutside(event: Event) {
      if (mobileMenu) {
        if (
          refMenu.current &&
          !refMenu.current.contains(event.target as Node)
        ) {
          toggleMenu();
        }
      }

      if (stepsMenu) {
        if (navRef.current && !navRef.current.contains(event.target as Node)) {
          toggleStepsMenu();
        }
      }
    }

    window.addEventListener("mousedown", handleClickOutside);
    window.addEventListener("scroll", handleClickOutside);

    return () => {
      window.removeEventListener("mousedown", handleClickOutside);
      window.removeEventListener("scroll", handleClickOutside);
    };
  });

  useEffect(() => {
    function handleResize() {
      if (mobileMenu) {
        toggleMenu();
      }

      if (stepsMenu) {
        toggleStepsMenu();
      }
    }

    window.addEventListener("resize", handleResize);

    return () => window.removeEventListener("resize", handleResize);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const openApplicationSelector = () => {
    setIsApplicationSelectorOpen(true);
  };

  const steps = !isDelegate &&
    !isIdentifyCustomer &&
    isNotPreliminaryBidForm && (
      <MenuContext.Provider
        value={{
          showSteps: toggleStepsMenu,
          stepsIsVisible: stepsMenu,
          openApplicationSelector,
        }}
      >
        {MiddleNavbarComponent ? <MiddleNavbarComponent /> : null}
      </MenuContext.Provider>
    );

  return (
    <>
      <header
        ref={refHeader}
        style={{ transition: "box-shadow 0.4s ease 0s, top 0.4s ease 0s" }}
        className={twMerge(
          "tw-left-0 tw-top-0 tw-z-[100] tw-select-none",
          "tw-h-14 lg:tw-h-16",
          "tw-fixed tw-w-full tw-shrink-0 tw-grow-0 tw-border-b tw-bg-white",
          {
            "tw-top-[-56px] lg:tw-top-[-64px]": scrollDown && !isMobile,
          },
        )}
      >
        <Container
          fluid
          className={twMerge("tw-h-full", {
            "tw-px-6": isMobile,
          })}
        >
          <div className="tw-flex tw-h-full tw-items-center tw-justify-between tw-text-primary">
            <Logo
              className={twMerge(logoStyling(settings.countryCode))}
              href={mainLogoHref}
            />

            <div className="tw-flex tw-items-center">
              {!isDelegate && !isIdentifyCustomer && (
                <>
                  <Language />

                  {!isMobile && !isEmpty(user) && (
                    <div className="tw-shrink-0">
                      <Button
                        variant="text"
                        className="tw-px-4"
                        onClick={onContactCustomerService}
                      >
                        {t("menu.support")}
                      </Button>
                    </div>
                  )}

                  {!isMobile && (
                    <CustomerServiceContactResponsiveDialog
                      open={openContactDrawer}
                      setOpen={setOpenContactDrawer}
                    />
                  )}

                  {!isMobile && isNotLoginPage && !isEmpty(user) && (
                    <MenuContext.Provider
                      value={{
                        openApplicationSelector,
                      }}
                    >
                      {applicationSelector}
                    </MenuContext.Provider>
                  )}

                  {isNotLoginPage && isEmpty(user) && (
                    <Button
                      size={isMobile ? "sm" : "md"}
                      variant="outlined"
                      className="xl:tw-ml-4 xl:tw-min-w-36 xl:tw-max-w-[11.25rem]"
                      onClick={() => {
                        navigate("/login");
                      }}
                    >
                      {t("menu.login")}
                    </Button>
                  )}

                  {!isMobile && !isEmpty(user) && (
                    <Button
                      variant="outlined"
                      className="tw-ml-4"
                      style={{ width: "8.75rem" }}
                      onClick={onLogout}
                    >
                      {t("menu.logout")}
                    </Button>
                  )}

                  {isMobile && isNotLoginPage && !isEmpty(user) && (
                    <MenuButton
                      className="-tw-mr-2"
                      icon={UserSVG}
                      onClick={toggleMenu}
                    />
                  )}
                </>
              )}

              {isIdentifyCustomer && <Language />}
            </div>
          </div>
        </Container>
      </header>

      {isNotLoginPage &&
        !isDelegate &&
        !isIdentifyCustomer &&
        isNotPreliminaryBidForm && (
          <>
            <nav
              ref={navRef}
              style={{
                transition: "box-shadow 0.4s ease 0s, top 0.4s ease 0s",
              }}
              className={twMerge(
                "tw-left-0 tw-z-[99] lg:tw-top-[64px] tw-top-[56px] xl:tw-h-20",
                "tw-flex-col xl:tw-flex-row tw-fixed tw-max-h-full tw-bg-white tw-border-b tw-flex tw-w-full",
                {
                  "tw-shadow-md": sticky || stepsMenu,
                  "!tw-top-0": scrollDown && !isMobile,
                  "tw-border-white": sticky || stepsMenu,
                },
              )}
            >
              {!isMobile ? (
                <div className="tw-flex tw-h-full tw-w-full tw-justify-center">
                  <Container className="tw-h-full">
                    <div className="tw-flex tw-h-full tw-justify-center">
                      {steps}
                    </div>
                  </Container>
                </div>
              ) : (
                <>{steps}</>
              )}
            </nav>

            {isMobile && (
              <>
                <AnimatePresence>
                  {mobileMenu && (
                    <>
                      <motion.div
                        variants={motionMenu}
                        initial="hidden"
                        animate="visible"
                        exit="hidden"
                        key="menu"
                      >
                        <MobileMenuDrawer ref={refMenu} onClose={toggleMenu}>
                          <div className="tw-flex tw-flex-col tw-items-center tw-rounded tw-bg-gray tw-p-4 md:tw-flex-row md:tw-gap-4">
                            {!isEmpty(user) && applicationSelector && (
                              <MenuContext.Provider
                                value={{ toggleMenu, openApplicationSelector }}
                              >
                                {applicationSelector}
                              </MenuContext.Provider>
                            )}

                            {!isEmpty(user) && (
                              <Button
                                variant="text"
                                className="tw-px-4"
                                onClick={onContactCustomerService}
                              >
                                {t("menu.support")}
                              </Button>
                            )}

                            {!isEmpty(user) && (
                              <Button
                                variant="text"
                                className="tw-px-4"
                                onClick={onLogout}
                              >
                                {t("menu.logout")}
                              </Button>
                            )}
                          </div>
                        </MobileMenuDrawer>
                      </motion.div>

                      <motion.div
                        variants={motionBackdrop}
                        initial="hidden"
                        animate="visible"
                        exit="hidden"
                        transition={{
                          default: { duration: 0.4, ease: "easeOut" },
                        }}
                        className={twMerge(
                          "tw-top-0 tw-left-0 tw-bg-purple-150/75 tw-z-[98]",
                          "tw-fixed tw-h-screen tw-w-full",
                        )}
                        key="backdrop"
                      />
                    </>
                  )}
                </AnimatePresence>

                <CustomerServiceContactResponsiveDialog
                  open={openContactDrawer}
                  setOpen={setOpenContactDrawer}
                />
              </>
            )}
          </>
        )}
      <ChangeApplicationModal
        open={isApplicationSelectorOpen}
        setOpen={setIsApplicationSelectorOpen}
      />
    </>
  );
};
